import { isValid, parse } from 'date-fns';
import { useMemo } from 'react';
import { clearTime } from 'lib/date';
import { queryDateFormat } from 'lib/transport';
import { ReconciliationPageQuery } from 'pages/ReconciliationPage';
import { BankAccount, TransactionDateType, TransactionState } from 'types/dto';

/**
 * Parse a query string value into a Date object
 *
 * @param {string} value Value to parse
 */
const parseQueryDate = (value?: string) => {
  // Instance of the current date
  const today = clearTime(new Date());

  // Parse specified value into a date object
  const result = parse(value ?? '', queryDateFormat, today);

  // Use parsed date if the value was in a valid format or fall back to today
  return isValid(result) ? result : undefined;
};

/**
 * Parse a query string value into a transaction date type
 *
 * @param {string} value Value to parse
 */
const parseQueryTransactionDateType = (value?: string) => {
  // List of valid transaction date types
  const valid: Array<TransactionDateType> = ['PAYMENT', 'POSTING'];

  return typeof value === 'undefined' || (valid as Array<string>).includes(value)
    ? (value as TransactionDateType)
    : undefined;
};

/**
 * Parse a query string value into a transaction state
 *
 * @param {string} value Value to parse
 */
const parseQueryTransactionState = (value?: string) => {
  // List of valid transaction states
  const valid: Array<TransactionState> = ['IGNORED', 'MATCHED', 'UNMATCHED'];

  return typeof value === 'undefined' || (valid as Array<string>).includes(value)
    ? (value as TransactionState)
    : undefined;
};

/**
 * Hook parsing the current query object into individual reconciliation page parameters
 *
 * @param {ReconciliationPageQuery} query Source query to parse
 * @param {Array<BankAccount>} bankAccountList List of all available bank accounts
 * @param {Date} defaultStartDate Default search start date
 */
export const useReconciliationQueryParams = (
  query: ReconciliationPageQuery,
  bankAccountList: Array<BankAccount>,
  defaultStartDate: Date,
) => {
  // Instance of the current date
  const today = useMemo(() => clearTime(new Date()), []);

  // Parse bank account identifiers
  const accountIdsBank = useMemo(() => new Set(query.ab ?? []), [query.ab]);
  const accountIdsSystem = useMemo(() => new Set(query.as ?? []), [query.as]);

  // Extract bank accounts
  const queryAccountsBank = useMemo(
    () => bankAccountList.filter(account => accountIdsBank.has(account.id)),
    [accountIdsBank, bankAccountList],
  );

  // Extract system account ids
  const queryAccountsSystem = useMemo(
    () => bankAccountList.filter(account => accountIdsSystem.has(account.id)),
    [accountIdsSystem, bankAccountList],
  );

  // Extract bank account ids
  const queryAccountIdsBank = useMemo(
    () => queryAccountsBank.map(account => account.id),
    [queryAccountsBank],
  );

  // Extract system account ids
  const queryAccountIdsSystem = useMemo(
    () => queryAccountsSystem.map(account => account.id),
    [queryAccountsSystem],
  );

  // Attempt to parse date parameters
  const queryStmtDateFromBank = useMemo(
    () => parseQueryDate(query.sfb) ?? defaultStartDate,
    [defaultStartDate, query.sfb],
  );
  const queryStmtDateToBank = useMemo(() => parseQueryDate(query.stb) ?? today, [query.stb, today]);
  const queryStmtDateFromSystem = useMemo(
    () => parseQueryDate(query.sfs) ?? defaultStartDate,
    [defaultStartDate, query.sfs],
  );
  const queryStmtDateToSystem = useMemo(
    () => parseQueryDate(query.sts) ?? today,
    [query.sts, today],
  );

  // Parse transaction date type parameters
  const queryTransactionDateTypeBank = useMemo(
    () => parseQueryTransactionDateType(query.dtb),
    [query.dtb],
  );
  const queryTransactionDateTypeSystem = useMemo(
    () => parseQueryTransactionDateType(query.dts),
    [query.dts],
  );

  // Parse transaction state parameters
  const queryTransactionStateBank = useMemo(() => parseQueryTransactionState(query.sb), [query.sb]);
  const queryTransactionStateSystem = useMemo(
    () => parseQueryTransactionState(query.ss),
    [query.ss],
  );

  return {
    queryAccountIdsBank,
    queryAccountIdsSystem,
    queryAccountsBank,
    queryAccountsSystem,
    queryStmtDateFromBank,
    queryStmtDateFromSystem,
    queryStmtDateToBank,
    queryStmtDateToSystem,
    queryTransactionDateTypeBank,
    queryTransactionDateTypeSystem,
    queryTransactionStateBank,
    queryTransactionStateSystem,
  };
};
